import { Handle, Position } from '@xyflow/react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { type EntitySuggestion, type User } from '@bliks-io/contracts';
import SuccessorCard, { type SuccessorCardType } from '../components/cards/SuccessorCard';
import type { SuccessorChangeHandler, SuccessorUpdateData } from '../types';

export interface SuccessorCardNodeData {
  users: User[];
  value: Required<SuccessorUpdateData>;
  onChange: SuccessorChangeHandler;
  submitted?: boolean;
  type?: SuccessorCardType;
  ruleNumber?: number;
  /** Recording ID — required for the system-request flow inside the picker. */
  recordingId?: string;
  /** Process ID — drives the "for this process" picker bucket. */
  processId?: string;
  /** Start event node behind the starter card — see SuccessorCard. */
  startEventNodeId?: string;
  /** Draft host: suggestions post to the draft routes (see SuccessorEntityPickerModal). */
  draftProcessId?: string;
  /** Create teams / org functions / systems outright instead of suggesting them. */
  allowCreate?: boolean;
  /** Optional rule id for decision-rule successors. */
  sourceRuleId?: string;
  /** Entity suggestions on the recording — used to render ghost rows. */
  entitySuggestions?: EntitySuggestion[];
}

const SuccessorCardNode = ({ data }: { data: SuccessorCardNodeData }) => {
  const { t } = useTranslation();

  return (
    <div className="nopan relative">
      {/* Display rule number above the card if this is a decision rule successor */}
      {data.ruleNumber && (
        <div className="absolute left-0 text-xs font-semibold text-info -top-8">
          {t('viralRecording.labels.ruleNumber', { number: data.ruleNumber })}
        </div>
      )}

      <Handle
        id="target"
        type="target"
        position={Position.Left}
        style={{
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          width: '10px',
          height: '10px'
        }}
      />

      <SuccessorCard
        users={data.users || []}
        submitted={data.submitted || false}
        value={data.value}
        onChange={data.onChange}
        type={data.type}
        recordingId={data.recordingId}
        processId={data.processId}
        startEventNodeId={data.startEventNodeId}
        draftProcessId={data.draftProcessId}
        allowCreate={data.allowCreate}
        sourceRuleId={data.sourceRuleId}
        entitySuggestions={data.entitySuggestions}
      />
    </div>
  );
};

export default memo(SuccessorCardNode);
